import {useState, useEffect} from "react";
import {
  LogOut,
  User,
  ChevronDown,
  Phone,
  X,
  CircleUserRound,
} from "lucide-react";

const CustomInput = ({
  label,
  id,
  value,
  onChange,
  type = "text",
  placeholder = "",
}) => (
  <div className="flex flex-col space-y-1">
    <label htmlFor={id} className="text-sm font-medium text-gray-700">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 shadow-sm"
    />
  </div>
);

const OutboundCallModal = ({
  isOpen,
  closeModal,
  queueOptions,
  setIsModalOpen,
  onOutbound,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+52");
  const [selectedQueue, setSelectedQueue] = useState("");

  if (!isOpen) return null;

  const countryCodeOptions = [
    {
      value: "+52",
      name: "+52 (Mexico)",
    },
    {
      value: "+1",
      name: "+1 (US & Canada)",
    },
  ];

  const handleSubmit = () => {
    setIsModalOpen(false);
    console.log(
      `Country Code: ${countryCode}, Phone Number: ${phoneNumber}, Selected Queue: ${selectedQueue}`
    );

    console.log(`Making Outbound Call!!`);
    onOutbound(countryCode, phoneNumber, selectedQueue);
    setCountryCode("+52");
    setPhoneNumber("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={closeModal}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-2xl font-semibold text-gray-800">
            Outbound Call
          </h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          <div className="flex space-x-4">
            {/* Country Code */}
            <div className="w-1/3 flex flex-col space-y-1">
              <label
                htmlFor="countryCode"
                className="text-sm font-medium text-gray-700"
              >
                Code
              </label>
              <select
                id="countryCode"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition shadow-sm appearance-none bg-white pr-8"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='currentColor'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "1.5em 1.5em",
                }}
              >
                {countryCodeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone Number - Using CustomInput */}
            <div className="w-2/3">
              <CustomInput
                label="Phone Number"
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="202-555-0123"
                autoFocus // Recommended for UX
              />
            </div>
          </div>

          {/* Queue Selection */}
          <div className="flex flex-col space-y-1">
            <label
              htmlFor="queue"
              className="text-sm font-medium text-gray-700"
            >
              Target Queue
            </label>
            <select
              id="queue"
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition shadow-sm appearance-none bg-white pr-8"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='currentColor'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1.5em 1.5em",
              }}
            >
              <option value="" disabled>
                Select a routing queue...
              </option>
              {queueOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end pt-4 space-x-3">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              disabled={!phoneNumber || !selectedQueue}
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition shadow-md disabled:opacity-50"
            >
              Call
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileModal = ({isOpen, onClose, agentInfo}) => {
  if (!isOpen || !agentInfo) return null;

  const {
    agentName,
    agentARN,
    agentPermissions,
    agentDialableCountries,
    agentRoutingProfile,
  } = agentInfo;

  return (
    // Modal Overlay
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm transition-opacity duration-300">
      {/* Modal Card - Adjusted max-width and max-height */}
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-11/12 max-w-2xl max-h-[90vh] flex flex-col m-4 transform transition-all duration-300 scale-100 opacity-100"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header - Fixed at top */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center">
            <CircleUserRound className="w-6 h-6 text-[#2ba9cd] mr-3" />
            <h3 id="modal-title" className="text-xl font-normal text-[#2ba9cd]">
              Agent Profile
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body Content - Scrollable area */}
        <div className="p-6 overflow-y-auto custom-scrollbar">
          <div className="bg-[#c8f3ff]/50 p-4 rounded-lg border border-[#2ba9cd]">
            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Name</div>
              <div className="font-bold w-2/3 ml-2">{agentName}</div>
            </div>

            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Agent ID</div>
              <div className="font-bold w-2/3 ml-2">
                {agentARN.split("/").pop()}
              </div>
            </div>

            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Routing Profile</div>
              <div className="font-bold w-2/3 ml-2">
                {agentRoutingProfile.name}
              </div>
            </div>

            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Permissions</div>
              <div className="font-bold w-2/3 ml-2">
                <ul className="list-disc list-inside">
                  {agentPermissions.map((permission, index) => (
                    <li key={index} className="font-medium text-gray-800">
                      {permission}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Dialable Countries</div>
              <div className="font-bold w-2/3 ml-2">
                <ul className="list-disc list-inside">
                  {agentDialableCountries.map((country, index) => (
                    <li key={index} className="font-medium text-gray-800">
                      {country}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-sm font-normal text-gray-700 mb-3 flex items-start">
              <div className="font-bold w-1/3 ml-2">Queues</div>
              <div className="font-bold w-2/3 ml-2">
                <ul className="list-disc list-inside">
                  {agentRoutingProfile.queues
                    .filter((queue) => queue?.name?.trim()) // Only include queues with a non-empty name
                    .map((queue, index) => (
                      <li key={index} className="font-medium text-gray-800">
                        {queue.name}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-xl flex justify-end space-x-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Header = ({onLogout, agentInfo, agentQueues, onOutbound}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profile, setProfile] = useState(false);

  const [queueOptions, setQueueOptions] = useState([
    {
      value: "",
      name: "--Please Select--",
    },
  ]);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    onLogout();
  };

  const handleProfileView = () => {
    setIsDropdownOpen(false);
    setProfile(true);
  };

  const isValidPhoneNumber = (number) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(number);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    let queues = agentQueues?.filter((x) => {
      return x && x !== null;
    });
    if (queues && queues.length > 0) {
      setQueueOptions([
        {
          value: "",
          name: "--Please Select--",
        },
        ...queues,
      ]);
    }
  }, [agentQueues]);

  console.log(`Agent Info in Header: ${JSON.stringify(agentInfo)}`);

  return (
    <header className="bg-white shadow-md border-b border-gray-100 sticky top-0 z-10 h-15">
      <div className="w-full px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <img
            src="/images/ppro_logo.jpg"
            alt="App Logo"
            className="h-[40px]"
          />
        </div>

        {agentInfo ? (
          <div className="flex items-center space-x-4">
            <Phone
              className="h-5 w-5 text-[#2ba9cd] cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            />
            {/* Profile and Actions (Dropdown implementation) */}

            <div className="relative">
              {/* Profile Button (Toggles Dropdown) */}
              <button
                onClick={toggleDropdown}
                className="flex items-center space-x-2 bg-gray-100 p-2 rounded-full pr-4 shadow-inner hover:bg-gray-200 transition duration-150 focus:outline-none focus:ring-2 focus:ring-[#2ba9cd] focus:ring-offset-2"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
              >
                <div
                  className={`h-5 w-5 ${
                    agentInfo.agentState.name === "Available"
                      ? "bg-green-500 border-green-200"
                      : "bg-gray-400 border-gray-200"
                  } rounded-full flex items-center justify-center border-2`}
                ></div>
                <span className="text-sm font-semibold text-gray-700 hidden sm:inline">
                  {agentInfo.agentName}
                </span>
                {/* Chevron icon to indicate dropdown status */}
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 hidden sm:inline transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-20 origin-top-right transform transition ease-out duration-200"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <div className="block px-4 py-2 text-xs text-gray-400 border-b border-gray-100">
                    Signed in as{" "}
                    <span className="font-medium text-gray-600 truncate">
                      {agentInfo.agentName}
                    </span>
                  </div>

                  {/* Example Menu Item: View Profile */}
                  <a
                    href="#"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    onClick={handleProfileView}
                  >
                    <User className="mr-2 h-4 w-4 text-[#2ba9cd]" />
                    View Profile
                  </a>

                  {/* Logout Button */}
                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left border-t mt-1"
                    role="menuitem"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <OutboundCallModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          queueOptions={queueOptions}
          setIsModalOpen={setIsModalOpen}
          onOutbound={onOutbound}
        />
      )}

      {/* The Modal */}
      <ProfileModal
        isOpen={profile === true}
        onClose={() => setProfile(false)}
        agentInfo={agentInfo}
      />
    </header>
  );
};

export default Header;
